type EventCallback = (...args: unknown[]) => void;

/**
 * Simple event emitter for game-wide events.
 */
export class EventEmitter {
  private listeners: Map<string, Set<{ callback: EventCallback; context?: unknown; once?: boolean }>> = new Map();

  /**
   * Register an event listener
   */
  on(event: string, callback: EventCallback, context?: unknown): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add({ callback, context });
    return this;
  }

  /**
   * Register a one-time event listener
   */
  once(event: string, callback: EventCallback, context?: unknown): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add({ callback, context, once: true });
    return this;
  }

  /**
   * Remove an event listener
   */
  off(event: string, callback?: EventCallback, context?: unknown): this {
    if (!this.listeners.has(event)) return this;

    if (!callback) {
      // Remove all listeners for this event
      this.listeners.delete(event);
    } else {
      const listeners = this.listeners.get(event)!;
      for (const listener of listeners) {
        if (listener.callback === callback && (!context || listener.context === context)) {
          listeners.delete(listener);
        }
      }
    }
    return this;
  }

  /**
   * Emit an event
   */
  emit(event: string, ...args: unknown[]): this {
    if (!this.listeners.has(event)) return this;

    const listeners = this.listeners.get(event)!;
    const toRemove: Array<{ callback: EventCallback; context?: unknown; once?: boolean }> = [];

    for (const listener of listeners) {
      if (listener.context) {
        listener.callback.apply(listener.context, args);
      } else {
        listener.callback(...args);
      }
      if (listener.once) {
        toRemove.push(listener);
      }
    }

    // Remove one-time listeners
    for (const listener of toRemove) {
      listeners.delete(listener);
    }

    return this;
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(event?: string): this {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
    return this;
  }
}
